/**
 * @file MapOfAgHistory.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-18
 * @modified 2026-09-18
 * @description Every Map of Ag report that has gone out, and the means to
 *              produce any of them again.
 *
 *              A re-export is rendered from the payload stored WITH THAT RUN,
 *              never by re-running its period. Formulations, unit weights and
 *              even the invoices behind it move; re-running March next year
 *              answers a different question. When a processor queries a figure,
 *              the only useful document is the one they were actually sent.
 *
 *              Runs from before the history feature have no stored payload and
 *              say so, rather than offering a button that would quietly produce
 *              something else.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './InvoiceDetailReport.css';
import './MapOfAgReport.css';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import MapOfAgExportModal from './MapOfAgExportModal';
import DocumentPreviewModal from './DocumentPreviewModal';
import { useSlideOutButton } from './SlideOutButton';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { formatDateShort } from '../utils/dateFormatters';

const t4 = (n) => (n === null || n === undefined || n === ''
  ? '—'
  : Number(n).toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 }));

const when = (ts) => (ts
  ? new Date(ts).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
  : '—');

const MODE_ICON = { download: 'download', view: 'visibility', email: 'mail' };

/** A date or a number reads newest/largest first; a name reads A–Z. */
const DESC_FIRST = ['created_at', 'from_date', 'customers', 'tonnes'];

const SORTERS = {
  created_at: (r) => new Date(r.created_at).getTime(),
  created_by_name: (r) => String(r.created_by_name || '').toLowerCase(),
  delivery_mode: (r) => `${r.delivery_mode || ''} ${r.format || ''}`.toLowerCase(),
  from_date: (r) => String(r.from_date || ''),
  processors: (r) => String(r.processors?.[0]?.name || '').toLowerCase(),
  customers: (r) => Number(r.customers || 0),
  tonnes: (r) => Number(r.tonnes || 0)
};

/** A sortable header, in the Search Contacts shape. */
function SortTh({ field, label, className = '', sortField, sortDirection, handleSort }) {
  return (
    <th className={`sortable ${className}`.trim()} onClick={() => handleSort(field)}>
      {label} {sortField === field && (sortDirection === 'asc' ? '▲' : '▼')}
    </th>
  );
}

function MapOfAgHistory({ user }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  // The run being exported, once someone has chosen how.
  const [exportFor, setExportFor] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [alert, setAlert] = useState({ isOpen: false, message: '', type: 'info' });

  const say = (message, type = 'info') => setAlert({ isOpen: true, message, type });
  useSlideOutButton([runs.length, busy]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/map-of-ag/runs`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load the history');
      setRuns(data.runs || []);
    } catch (e) {
      say(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Same shape as Search Contacts: click a header to sort, click again to turn
  // it round, with the ▲/▼ in the header itself.
  const handleSort = (field) => {
    if (field === sortField) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection(DESC_FIRST.includes(field) ? 'desc' : 'asc'); }
  };

  const ctxMenu = useTableContextMenu({ rows: runs, getRowId: (r) => r.id });

  const sorted = useMemo(() => {
    const read = SORTERS[sortField] || SORTERS.created_at;
    const dir = sortDirection === 'desc' ? -1 : 1;
    return [...runs].sort((a, b) => {
      const x = read(a);
      const y = read(b);
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return 0;
    });
  }, [runs, sortField, sortDirection]);

  const reExport = async (run, mode, { format = 'xlsx', to } = {}) => {
    setBusy(`${run.id}-${mode}`);
    try {
      const res = await fetch(`${API_URL}/api/map-of-ag/runs/${run.id}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          // A preview is always a PDF: there is nothing to look at in a browser
          // for a workbook.
          format: mode === 'view' ? 'pdf' : format,
          mode,
          ...(mode === 'email' ? { to: to || user?.email } : {})
        })
      });

      if (mode === 'email') {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Send failed');
        setExportFor(null);
        say(data.message, 'success');
      } else {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Could not produce it');
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        {
          const a = document.createElement('a');
          a.href = url;
          // The server names it; this is only the fallback if it did not.
          a.download = run.filename || `EA_MofA.${mode === 'view' ? 'pdf' : format}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        }
      }
    } catch (e) {
      say(e.message, 'error');
    } finally {
      setBusy(null);
    }
  };

  /**
   * One list of actions, behind the three-dot menu and the right-click menu
   * alike — two menus offering different things would be two menus to keep in
   * step. All three are read-safe: producing a copy of a document that has
   * already gone out changes nothing.
   */
  const buildRowActions = (r) => {
    const why = r.can_re_export
      ? null
      : 'This export predates the history feature, so its contents were not kept';
    return [
      {
        key: 'view',
        readSafe: true,
        icon: 'visibility',
        label: 'Preview',
        title: why || 'Open it as a PDF in a new tab',
        disabled: !r.can_re_export || !!busy,
        onClick: () => setPreviewFor(r)
      },
      {
        key: 'export',
        readSafe: true,
        icon: 'ios_share',
        label: 'Export…',
        title: why || 'Choose a format and download or email it',
        disabled: !r.can_re_export || !!busy,
        onClick: () => setExportFor(r)
      }
    ];
  };

  return (
    <div className="map-of-ag-report invoice-detail-report map-of-ag-history">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Report history</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              Every report that has gone out. Producing one again uses the figures as they were
              sent, not as they would be calculated today.
            </span>
          </span>
        </div>

      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="table-container">
          <table className="data-table history-table" onContextMenu={ctxMenu.onTableContextMenu}>
            <thead>
              <tr>
                <SortTh field="created_at" label="Exported" {...{ sortField, sortDirection, handleSort }} />
                <SortTh field="created_by_name" label="By" {...{ sortField, sortDirection, handleSort }} />
                <SortTh field="delivery_mode" label="How" {...{ sortField, sortDirection, handleSort }} />
                <SortTh field="from_date" label="Period covered" {...{ sortField, sortDirection, handleSort }} />
                <SortTh field="processors" label="Processors" {...{ sortField, sortDirection, handleSort }} />
                <SortTh field="customers" label="Customers" className="num" {...{ sortField, sortDirection, handleSort }} />
                <SortTh field="tonnes" label="Tonnes" className="num" {...{ sortField, sortDirection, handleSort }} />
                <th className="actions-header no-sort" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} className="line-row">
                  <td className="hist-when">{when(r.created_at)}</td>
                  <td>{r.created_by_name || '—'}</td>
                  <td>
                    {/* The flex goes on a div inside the cell, not on the td —
                        a flex td loses the row's natural height. */}
                    <div className="hist-how">
                      <span className="material-symbols-rounded" title={r.delivery_mode}>
                        {MODE_ICON[r.delivery_mode] || 'description'}
                      </span>
                      <span className="hist-format">{String(r.format || '').toUpperCase()}</span>
                      {r.emailed_to && (
                        <span className="hist-email" title={r.email_subject || ''}>{r.emailed_to}</span>
                      )}
                    </div>
                  </td>
                  <td className="hist-when">
                    {formatDateShort(r.from_date)} – {formatDateShort(r.to_date)}
                  </td>
                  <td>
                    <span className="hist-processors">
                      {r.processors.map((p) => (
                        <span key={p.name} className="hist-processor">
                          {p.name}<span className="hist-processor-count">{p.customers}</span>
                        </span>
                      ))}
                      {!r.processors.length && <span className="muted">—</span>}
                    </span>
                  </td>
                  <td className="num">{r.customers}</td>
                  <td className="num">{t4(r.tonnes)}</td>
                  <td>
                    <div className="actions-cell">
                      <RowActionsMenu actions={buildRowActions(r)} />
                    </div>
                  </td>
                </tr>
              ))}
              {!sorted.length && (
                <tr><td colSpan={8} className="report-empty">No reports have been produced yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          {
            key: 'newest',
            readSafe: true,
            icon: 'schedule',
            label: 'Newest first',
            disabled: sortField === 'created_at' && sortDirection === 'desc',
            onClick: () => { setSortField('created_at'); setSortDirection('desc'); }
          },
          { key: 'refresh', readSafe: true, icon: 'refresh', label: 'Refresh', onClick: load }
        ]}
      />

      <DocumentPreviewModal
        isOpen={!!previewFor}
        endpoint={previewFor ? `/api/map-of-ag/runs/${previewFor.id}/preview-document` : null}
        payload={previewFor ? { id: previewFor.id } : null}
        title="Map of Ag report, as it was sent"
        onClose={() => setPreviewFor(null)}
      />

      <MapOfAgExportModal
        isOpen={!!exportFor}
        title="Export this report again"
        busy={!!busy}
        defaultEmail={exportFor?.emailed_to || user?.email || ''}
        defaultFormat={exportFor?.format || 'xlsx'}
        onExport={({ format, mode, to }) => reExport(exportFor, mode, { format, to })}
        onClose={() => setExportFor(null)}
      />

      <AlertDialog
        isOpen={alert.isOpen}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, isOpen: false })}
      />
    </div>
  );
}

export default MapOfAgHistory;
